/*
All Rights Reserved. Permission to use and distribute this software, source code and its documentation is hereby granted on the terms
and conditions set out in the License Agreement dated August 10, 2018 (‘License Agreement’) between Prashant Sharma and China Guangdong
Jingteng Technology Co., Ltd. This copyright notice shall appear in all copies and distributions to the extent permitted in the License
Agreement. This Software, source code and its documentation may not be used without the prior written consent of Prashant Sharma in any
manner whatsoever except to the extent specified in the License Agreement. Contact Prashant Sharma, B-801, Purva Panorama, Bannerghatta Road,
Bangalore – 560 076, +91 9731217354, spidersharma@gmail.com for any queries on this Software.

IN NO EVENT SHALL PRASHANT SHARMA BE LIABLE TO ANY PARTY FOR DIRECT, INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOST
PROFITS, ARISING OUT OF THE USE OF THIS SOFTWARE AND ITS DOCUMENTATION, EVEN IF PRASHANT SHARMA HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
DAMAGE.
PRASHANT SHARMA SPECIFICALLY DISCLAIMS ANY WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
FOR A PARTICULAR PURPOSE. THE SOFTWARE AND ACCOMPANYING DOCUMENTATION, IF ANY, PROVIDED HEREUNDER IS PROVIDED "AS IS". PRASHANT SHARMA HAS
NO OBLIGATION TO PROVIDE UPDATES, ENHANCEMENTS, OR MODIFICATIONS AND WILL, HOWEVER, PROVIDE TRAINING AND MAINTENANCE TO THE EXTENT AND FOR
THE FEES AS SET OUT IN SCHEDULE B TO THE LICENSE AGREEMENT.
 */

import {
  Vector3,
  CubeCamera,
  Scene,
  ShaderMaterial,
  UniformsUtils,
  NearestFilter,
  RGBAFormat,
  AmbientLight,
  WebGLCubeRenderTarget,
} from 'three'

import { normalMapCaptureMaterial, diamondMaterial } from './DiamondShaders'

class Diamond {
  constructor(mesh, material) {
    this.mesh_ = mesh
    this.mesh_.isDiamondMesh = true
    this.mesh_.userData.isGem = true
    this.mesh_.material = material.clone()
    this.mesh_.material._diamondMaterial = material
    //关联材质与模型，钻戒一个材质一个模型
    this.mesh_.material._mesh = mesh
    this.material = this.mesh_.material
    this.sparkles_ = []
    this.mesh_.material.materialType = 'DiamondMaterial'
    this.mesh_.material.isDiamondMaterial = true
    this.mesh_.geometry.computeBoundingBox()
    this.offset_ = new Vector3()
    this.mesh_.geometry.boundingBox.getCenter(this.offset_)
    this.mesh_.material.uniforms['centreOffset'].value.copy(this.offset_)

    this.mesh_.geometry.computeBoundingSphere()
    this.boundingRadius_ = this.mesh_.geometry.boundingSphere.radius
    this.mesh_.material.uniforms['radius'].value = this.boundingRadius_

    let self = this
    this.mesh_.onBeforeRender = function(renderer, scene, camera) {
      self.updateDiamond()
    }
    this.initUniformProperty()
  }
  initUniformProperty() {
    let self = this
    for (let key in self.material.uniforms) {
      Object.defineProperty(self.material, key, {
        set: function(value) {
          self.material.uniforms[key].value = value
        },
        get: function() {
          return self.material.uniforms[key].value
        },
      })
    }
  }
  getCentreOffset() {
    return this.offset_
  }

  getBoundingRadius() {
    return this.boundingRadius_
  }

  getMesh() {
    return this.mesh_
  }

  setPosition(x, y, z) {
    this.mesh_.position.set(x, y, z)
  }

  setRotation(x, y, z) {
    this.mesh_.rotation.set(x, y, z)
  }

  setQuaternion(x, y, z, w) {
    this.mesh_.quaternion.set(x, y, z, w)
  }

  setScale(x, y, z) {
    this.mesh_.scale.set(x, y, z)
    for (var i = 0; i < this.sparkles.length; i++) {
      this.sparkles_[i].setScale(x)
    }
  }

  setTransform(xForm) {
    this.mesh_.matrix.copy(xForm)
  }

  updateDiamond() {
    // let v = new Vector3();
    if (this.mesh_.material.isDiamondMaterial) {
      if (this.mesh_.material.uniforms['InverseModelMatrix']) {
        this.mesh_.updateMatrixWorld()
        let m1 = this.mesh_.matrixWorld
        let m2 = this.mesh_.material.uniforms['InverseModelMatrix'].value
        // m2.getInverse(m1)
        m2.copy(m1).invert()
      }
    }
  }

  addSparkle(sparkle) {
    this.sparkles_.push(sparkle)
  }
}

class DiamondMaterial {
  /*
  Load and initialize a new diamond material from a mesh.
  @params
  mesh : mesh from which the diamond should be made.
  envMap : Environment map to apply
  renderer: used to initialize normals cube map
  */
  constructor(mesh, envMap, renderer) {
    this.geometry_ = null
    this.mesh_ = null
    this.normalBakeHelperMesh_ = null

    this.envMap_ = envMap

    let cubRendertarget = new WebGLCubeRenderTarget(1024, {
      generateMipmaps: false,
      magFilter: NearestFilter,
      minFilter: NearestFilter,
      format: RGBAFormat,
    })
    this.cubeCamera_ = new CubeCamera(0.01, 100, cubRendertarget)
    this.localScene_ = new Scene()
    this.localScene_.add(this.cubeCamera_)
    this.material_ = new ShaderMaterial()
    var ambient = new AmbientLight()
    this.localScene_.add(ambient),
      (this.material_.materialType = 'DiamondMaterial')
    this.material_.isDiamondMaterial = true

    this.material_.extensions = diamondMaterial.extensions
    this.material_.defines = diamondMaterial.defines
    this.material_.uniforms = UniformsUtils.clone(diamondMaterial.uniforms)
    this.material_.uniforms['envMap'].value = this.envMap_
    this.material_.vertexShader = diamondMaterial.vertexShader
    this.material_.fragmentShader = diamondMaterial.fragmentShader
    this.needsNormalsUpdate_ = true

    this.mesh_ = mesh
    this.mesh_._currentDiamondMaterial = this
    this.normalBakeHelperMesh_ = this.mesh_.clone()

    this.normalBakeHelperMesh_.material = normalMapCaptureMaterial
    this.normalBakeHelperMesh_.geometry = mesh.geometry.clone()
    this.normalBakeHelperMesh_.geometry.center()

    this.geometry_ = mesh.geometry

    this.geometry_.computeBoundingBox()
    this.offset_ = new Vector3()
    this.geometry_.boundingBox.getCenter(this.offset_)

    this.geometry_.computeBoundingSphere()

    this.normalBakeHelperMesh_.position.set(0, 0, 0)
    this.normalBakeHelperMesh_.rotation.set(0, 0, 0)
    this.normalBakeHelperMesh_.quaternion.set(0, 0, 0, 1)
    this.normalBakeHelperMesh_.scale.set(1, 1, 1)

    this.localScene_.add(this.normalBakeHelperMesh_)
    this.prepareNormalsCubeMap(renderer)

    this.sparkles_ = []
  }

  /*
  Creates a copy of the diamond material.
  */
  clone() {
    const material = new ShaderMaterial()
    material.materialType = 'DiamondMaterial'
    material.isDiamondMaterial = true
    material.name = this.material_.name
    material.userData = this.material_.userData
    material.uniforms = UniformsUtils.clone(this.material_.uniforms)
    material.extensions = this.material_.extensions
    material.defines = this.material_.defines
    material.uniforms[
      'tCubeMapNormals'
    ].value = this.cubeCamera_.renderTarget.texture
    material.uniforms['envMap'].value = this.envMap_
    material.envMap = this.envMap_
    material.vertexShader = this.material_.vertexShader
    material.fragmentShader = this.material_.fragmentShader
    return material
  }

  /*
  Creates Normal Cube map, which is used to render the diamond.
  */
  prepareNormalsCubeMap(renderer) {
    if (this.needsNormalsUpdate_) {
      this.cubeCamera_.update(renderer, this.localScene_)
      this.material_.uniforms[
        'tCubeMapNormals'
      ].value = this.cubeCamera_.renderTarget
      this.needsNormalsUpdate_ = false
    }
  }

  dispose() {
    this.cubeCamera_.renderTarget.dispose()
    this.normalBakeHelperMesh_.geometry.dispose()
    this.material_.dispose()
  }
}

export { Diamond, DiamondMaterial }
